import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Campaign : famille "action de communication groupée".
 *
 * Action de communication groupée vers un public ciblé, avec segments, envois
 * planifiés et mesure des retombées (newsletter, appel à dons, relance
 * d'adhésion, invitation à un événement). Construite sur le modèle "famille +
 * type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("newsletter mensuelle",
 * "collecte de Noël") : il connaît la famille générique `campaign`. Le
 * vocabulaire métier est porté par le discriminant `type` (chaîne libre
 * déclarée par l'app) ; le `channel` (email, sms, push...) est un état métier à
 * part, distinct du discriminant.
 *
 * Public ciblé : `audienceGroupIds` liste les segments visés (réf. famille
 * `group`), et `audienceSnapshotContactIds` fige la liste des destinataires
 * réels au moment de l'envoi (réf. famille `contact`) : une photo figée, pas un
 * recalcul permanent. Ces deux listes sont de simples ids (aucun import d'un
 * autre schéma de famille).
 *
 * Sens des références : ce sont les familles AVAL qui pointent la campagne, pas
 * l'inverse. `message` (les envois individuels), `opportunity` (les pistes
 * générées), `donation` (les dons collectés) et `consent` (les preuves de
 * consentement) portent déjà `campaignId` de LEUR côté (relations
 * un-à-plusieurs) : les stocker ici serait la mauvaise cardinalité (même
 * doctrine que `order` pour ses pièces aval). On ne porte donc aucun de ces ids
 * côté campaign.
 *
 * Accès : v0 org-wide (même doctrine qu'asset), rien de sensible signalé.
 *
 * Convention de patron (cf. asset) :
 *   - `campaignCoreFields`        : champs MÉTIER seuls (sans les champs standard).
 *   - `CampaignSchema`            : famille complète = champs standard + métier.
 *   - `CampaignCreateInputSchema` : entrée fournie par l'appelant à la création
 *     (les champs standard sont remplis par le SDK). Valide AVANT écriture.
 */

/** Champs métier de la famille campaign (hors champs standard du socle). */
export const campaignCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "newsletter", "appel_dons", "relance"). */
  type: z.string().min(1),
  /** Canal de diffusion, chaîne libre pilotée par l'app (ex. "email", "sms", "push", "courrier"). Optionnel. */
  channel: z.string().min(1).optional(),
  /** Segments ciblés (réf. famille `group`) : liste d'ids. Optionnel. */
  audienceGroupIds: z.array(z.string()).optional(),
  /** Photo figée des destinataires réels (réf. famille `contact`) : liste d'ids. Optionnel. */
  audienceSnapshotContactIds: z.array(z.string()).optional(),
  /** Date d'envoi planifiée. Optionnel. */
  scheduledAt: z.date().optional(),
  /** Contenu du message diffusé (sujet, corps, gabarit...), structure libre pilotée par l'app. Optionnel. */
  content: z.record(z.string(), z.unknown()).optional(),
  /** Retombées mesurées (envoyés, ouverts, clics...), structure libre pilotée par l'app. Optionnel. */
  stats: z.record(z.string(), z.unknown()).optional(),
  /** Référence de campagne UTM pour le suivi des liens (ex. "noel-2026"). Optionnel. */
  utmRef: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille campaign (champs standard + champs métier). */
export const CampaignSchema = z.object({
  ...baseEntityFields,
  ...campaignCoreFields,
});

/** Type d'une campaign. */
export type Campaign = z.infer<typeof CampaignSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const CampaignCreateInputSchema = z.object({
  ...campaignCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une campaign. */
export type CampaignCreateInput = z.infer<typeof CampaignCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const CampaignUpdateInputSchema = z
  .object({ ...campaignCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une campaign. */
export type CampaignUpdateInput = z.infer<typeof CampaignUpdateInputSchema>;
